import { isValidTreatmentReferenceId } from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import type { PromoDisplayConfig } from '~/hooks/apis/promo/useGetPromoDisplayConfigs';

/** How a config reads in the picker: its promo code, region and what it targets. */
export function getPromoDisplayConfigLabel(config: PromoDisplayConfig): string {
  return [config.code_name, config.region, config.campaign || config.url_path].filter(Boolean).join(' · ');
}

/**
 * One entry in the picker: an active config, or the stored id on its own while
 * it has no active config to show (still checking, inactive or malformed).
 */
export type PromoDisplayConfigPickerOption = { id: string; config?: PromoDisplayConfig };

export function getPromoDisplayConfigPickerOptionLabel(option: PromoDisplayConfigPickerOption): string {
  return option.config ? getPromoDisplayConfigLabel(option.config) : option.id;
}

/**
 * The search results as picker options, with the selected entry added when the
 * search did not return it. MUI expects the selected value among the options;
 * the picker hides it from the list with `filterSelectedOptions`.
 */
export function getPromoDisplayConfigPickerOptions(
  selected: PromoDisplayConfigPickerOption | null,
  results: Array<PromoDisplayConfig>,
): Array<PromoDisplayConfigPickerOption> {
  const options = results.map((config) => ({ id: config.promo_display_config_id, config }));
  return selected && !options.some((option) => option.id === selected.id) ? [selected, ...options] : options;
}

/**
 * Where a promo display config treatment's id stands. `inactive` is an id the
 * router save will reject: no config with it is active for the brand.
 */
export type PromoDisplayConfigReferenceStatus =
  | { kind: 'empty' }
  | { kind: 'invalid-id' }
  | { kind: 'checking' }
  | { kind: 'lookup-failed'; message: string }
  | { kind: 'inactive' }
  | { kind: 'active'; config: PromoDisplayConfig };

type ActiveConfigLookup = {
  /** The active config with the id, `null` when there is none, `undefined` until the lookup answers. */
  data: PromoDisplayConfig | null | undefined;
  error: unknown;
};

/**
 * The id is judged exactly as the save judges it (untrimmed, same uuid check),
 * so the field never passes a value the save then rejects.
 */
export function getPromoDisplayConfigReferenceStatus(
  promoDisplayConfigId: string,
  lookup: ActiveConfigLookup,
): PromoDisplayConfigReferenceStatus {
  if (promoDisplayConfigId === '') {
    return { kind: 'empty' };
  }
  if (!isValidTreatmentReferenceId(promoDisplayConfigId)) {
    return { kind: 'invalid-id' };
  }
  if (lookup.data) {
    return { kind: 'active', config: lookup.data };
  }
  if (lookup.error) {
    return {
      kind: 'lookup-failed',
      message: lookup.error instanceof Error ? lookup.error.message : 'Unknown error',
    };
  }
  return lookup.data === null ? { kind: 'inactive' } : { kind: 'checking' };
}

/**
 * What the field says about the id. Only ids the save would reject are errors;
 * a failed lookup is not, because the save still checks the id itself.
 */
export function getPromoDisplayConfigReferenceMessage(
  status: PromoDisplayConfigReferenceStatus,
  brandTitle: string,
): { error: boolean; message?: string } {
  switch (status.kind) {
    case 'empty':
      return { error: false };
    case 'invalid-id':
      return { error: true, message: 'Not a valid ID. Pick a config from the list or paste its ID.' };
    case 'checking':
      return { error: false, message: 'Checking this ID…' };
    case 'lookup-failed':
      return { error: false, message: `Couldn't check this ID: ${status.message}` };
    case 'inactive':
      return {
        error: true,
        message: `No active ${brandTitle} promo display config has this ID. It may have expired, belong to another brand or not exist.`,
      };
    case 'active':
      return {
        error: false,
        message: `${status.config.header_text} · ID ${status.config.promo_display_config_id}`,
      };
  }
}
